import { useSnackbar } from 'notistack';
import { useApi } from '../../../shared/context';
import {
	Deal,
	PaymentScheduleType,
	PaymentScheduleTypeMap,
	ProductSubscription,
	RefinanceBenefitType,
	RefinanceBenefitTypeMap,
	SubscriptionFrequency,
	SubscriptionFrequencyMap,
	SuccessFeeType,
	SuccessFeeTypeMap,
} from '../../../shared/models';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroMaskedInput,
	CerebroPrimaryButton,
	CerebroText,
	CerebroYesNo,
} from '../../../shared/components';
import { EnumUtils, FormikUtils, NumberUtils } from '../../../shared/utils';
import { Field, Formik, FormikErrors } from 'formik';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys, useDefaultProductQuery, useExcludedLendersQuery, useStatesQuery } from '../../../shared/hooks';
import { Grid, Skeleton } from '@mui/material';
import { useParams } from 'react-router-dom';
import { LenderListDisplay } from '../LenderListDisplay';
import { MaxCurrencyLength } from '../../../shared/utils/platform';
import { getApiResponseData } from '../../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../../shared/utils/date';

interface CreateCompanySubscriptionDrawerProps {
	open: boolean;
	onClose: () => void;
	deal: Deal;
}

export const CreateCompanySubscriptionDrawer: React.FC<CreateCompanySubscriptionDrawerProps> = ({
	open,
	onClose,
	deal,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);
	const { guid } = useParams<'guid'>();
	const excludedLendersQuery = useExcludedLendersQuery(guid);
	const statesQuery = useStatesQuery();
	const productQuery = useDefaultProductQuery(guid);

	const excludedCountDefault = deal.excludedLenderCount.toString();
	const reducedCountDefault = deal.reducedFeeLenderCount.toString();

	const createSubscriptionMutation = useMutation(
		async ({ values }: CreateCompanySubscriptionMutationVariables) => {
			const successFeeCap = (NumberUtils.ParseDecimal(values.successFeeCap, undefined) ?? 0).toString();

			const dto = {
				term: NumberUtils.ParseDecimal(values.term),
				guaranteeTerms: NumberUtils.ParseDecimal(values.guaranteeTerms),
				dealsPerYear: NumberUtils.ParseDecimal(values.dealsPerYear),
				autoRenew: values.autoRenew,
				subscriptionFee: NumberUtils.ParseDecimal(values.subscriptionFee),
				firstYearSubscriptionFee: NumberUtils.ParseDecimal(values.firstYearSubscriptionFee),
				subscriptionFeePaymentSchedule:
					values.billingFrequency == 'Annually' ? values.subscriptionFeePaymentSchedule : 'Custom',
				additionalDealFee: NumberUtils.ParseDecimal(values.additionalDealFee),
				additionalDealFeePaymentSchedule: values.additionalDealFeePaymentSchedule,
				billingFrequency: values.billingFrequency,
				successFeeType: values.successFeeType,
				basisPoints: NumberUtils.ParseDecimal(values.successFeeType == 'Flat' ? values.basisPoints : ''),
				hasIncentivePricing: values.hasIncentivePricing,
				incentiveSubscriptionFee: NumberUtils.ParseDecimal(values.incentiveSubscriptionFee),
				incentiveExpirationDate: values.incentiveExpirationDate,
				companyName: values?.companyName,
				city: values?.city,
				postalCode: values.postalCode,
				state: values.state,
				address1: values?.address1,
				successFeePaymentSchedule: values.successFeePaymentSchedule,
				isDirectIntroDeal: values.isDirectIntroDeal,
				successFeeCap: successFeeCap,
				includeSbaExclusion: values.includeSbaExclusion,
				refinanceBenefitType: values.refinanceBenefitType,
				refinanceSuccessFeeDiscount: NumberUtils.ParseDecimal(values.refinanceSuccessFeeDiscount),
				excludedLendersCount: NumberUtils.ParseDecimal(values.excludedLendersCount),
				reducedFeeLenderDiscountPercent: NumberUtils.ParseDecimal(values.reducedFeeLenderDiscountPercent),
				reducedFeeLendersCount: NumberUtils.ParseDecimal(values.reducedFeeLendersCount),
				tailPeriodMonths: NumberUtils.ParseDecimal(values.tailPeriodMonths),
			};

			const { data, error } = await getApiResponseData<ProductSubscription>(
				api.post(CerebroApiEndpoints.Deals.CompanySubscriptions(deal.guid), dto)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(deal.guid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(deal.guid));
				snackbar.enqueueSnackbar('Company subscription successfully created.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading>Create Company Subscription</CerebroHeading>

			<Formik
				initialValues={{
					term: '1',
					guaranteeTerms: '1',
					dealsPerYear: '3',
					autoRenew: true,
					subscriptionFee: NumberUtils.FormatDecimal(productQuery.data?.setupFee) ?? '',
					firstYearSubscriptionFee: '',
					subscriptionFeePaymentSchedule:
						EnumUtils.GetPaymentScheduleTypeValue(productQuery.data?.accessFeePaymentScheduleType) ??
						'Net30',
					additionalDealFee: '0',
					additionalDealFeePaymentSchedule: 'Now',
					billingFrequency: productQuery.data?.subscriptionFrequency ?? 'Annually',
					successFeeType: productQuery.data?.successFeeType ?? 'Tiered',
					basisPoints: '',
					hasIncentivePricing: false,
					incentiveSubscriptionFee: '',
					incentiveExpirationDate: null,
					companyName: deal?.companyName,
					city: '',
					postalCode: '',
					state: '',
					address1: '',
					successFeePaymentSchedule:
						EnumUtils.GetPaymentScheduleTypeValue(productQuery.data?.successFeePaymentScheduleType) ??
						'Net7',
					isDirectIntroDeal: false,
					successFeeCap: NumberUtils.FormatDecimal(0),
					includeSbaExclusion: false,
					refinanceBenefitType: 'PercentDiscount',
					refinanceSuccessFeeDiscount: '33',
					excludedLendersCount: excludedCountDefault,
					reducedFeeLenderDiscountPercent: NumberUtils.FormatDecimal(
						productQuery.data?.reducedFeeLenderDiscountPercent
					),
					reducedFeeLendersCount: reducedCountDefault,
					tailPeriodMonths: '12',
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(createSubscriptionMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit, setFieldValue }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						{excludedLendersQuery.data && excludedLendersQuery.data.length > 0 && (
							<LenderListDisplay lenderType="Excluded" memberList={excludedLendersQuery.data} />
						)}

						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={12}>
								<CerebroLabel
									id="companyName"
									hint="Optional, will be included in contract and on invoices if provided"
								>
									Company Name
								</CerebroLabel>
								<Field as={CerebroInput} id="companyName" name="companyName" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel hint="Optional, will be included in contract and on invoices if provided">
									Company Address
								</CerebroLabel>
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="address1">Street</CerebroLabel>
								<Field as={CerebroInput} id="address1" name="address1" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="city">City</CerebroLabel>
								<Field as={CerebroInput} id="city" name="city" disabled={isSubmitting} />
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="state">State</CerebroLabel>
								{deal && statesQuery.isSuccess ? (
									<Field as={CerebroDropdown} name="state" disabled={isSubmitting}>
										{statesQuery.data.map(state => (
											<CerebroDropdownMenuItem key={state.code} value={state.code}>
												{state.name}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={6}>
								<CerebroLabel id="city">Zip Code</CerebroLabel>
								{deal && statesQuery.isSuccess ? (
									<Field
										as={CerebroInput}
										name="postalCode"
										disabled={isSubmitting}
										placeholder="Zip Code"
									/>
								) : (
									<Skeleton height={60} />
								)}
							</Grid>

							<Grid item xs={4}>
								<CerebroLabel required id="term">
									Term (years)
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="term"
									name="term"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('term', errors, touched)}
								/>
								{FormikUtils.IsInvalid('term', errors, touched) && (
									<CerebroText variant="error">{errors.term}</CerebroText>
								)}
							</Grid>
							<Grid item xs={4}>
								<CerebroLabel required id="guaranteeTerms">
									Guaranteed Term
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="guaranteeTerms"
									name="guaranteeTerms"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('guaranteeTerms', errors, touched)}
								/>
								{FormikUtils.IsInvalid('guaranteeTerms', errors, touched) && (
									<CerebroText variant="error">{errors.guaranteeTerms}</CerebroText>
								)}
							</Grid>
							<Grid item xs={4}>
								<CerebroLabel required id="dealsPerYear">
									Deals Per Year
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="dealsPerYear"
									name="dealsPerYear"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('dealsPerYear', errors, touched)}
								/>
								{FormikUtils.IsInvalid('dealsPerYear', errors, touched) && (
									<CerebroText variant="error">{errors.dealsPerYear}</CerebroText>
								)}
							</Grid>
							<Grid item xs={7}>
								<CerebroLabel required id="subscripbillingFrequencytionFee">
									Billing Frequency
								</CerebroLabel>
								<Field
									id="billingFrequency"
									as={CerebroDropdown}
									name="billingFrequency"
									disabled={isSubmitting}
									sx={{ marginTop: '29px' }}
								>
									{Object.entries(SubscriptionFrequencyMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>
							<Grid item xs={5}></Grid>
							<Grid item xs={5}>
								<CerebroLabel required id="subscriptionFee">
									Annual Subscription Fee
								</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="subscriptionFee"
									name="subscriptionFee"
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('subscriptionFee', errors, touched)}
								/>
								{FormikUtils.IsInvalid('subscriptionFee', errors, touched) && (
									<CerebroText variant="error">{errors.subscriptionFee}</CerebroText>
								)}
							</Grid>
							{values.billingFrequency == 'Annually' || values.billingFrequency == 'OneTime' ? (
								<Grid item xs={7}>
									<Field
										id="subscriptionFeePaymentSchedule"
										as={CerebroDropdown}
										name="subscriptionFeePaymentSchedule"
										disabled={isSubmitting}
										sx={{ marginTop: '29px' }}
									>
										{Object.entries(PaymentScheduleTypeMap).map(([value, description]) => (
											<CerebroDropdownMenuItem key={value} value={value}>
												{description}
											</CerebroDropdownMenuItem>
										))}
									</Field>
								</Grid>
							) : (
								<Grid item xs={7}>
									<CerebroText sx={{ marginTop: 3 }}>
										Subscription fee will be paid in{' '}
										{EnumUtils.GetSubscriptionFrequencyDescription(values.billingFrequency)}{' '}
										installments.
									</CerebroText>
								</Grid>
							)}
							<Grid item xs={5}>
								<CerebroLabel id="firstYearSubscriptionFee">1st Year Subscription Fee</CerebroLabel>
								<Field
									formatas="currency"
									as={CerebroMaskedInput}
									maxLength={MaxCurrencyLength}
									id="firstYearSubscriptionFee"
									name="firstYearSubscriptionFee"
									disabled={isSubmitting}
								/>
							</Grid>
							<Grid item xs={7}></Grid>
							<Grid item xs={5}>
								<CerebroLabel required id="successFeeType">
									Success Fee Type
								</CerebroLabel>
								<Field
									id="successFeeType"
									as={CerebroDropdown}
									name="successFeeType"
									disabled={isSubmitting}
								>
									{Object.entries(SuccessFeeTypeMap).map(([value, description]) => (
										<CerebroDropdownMenuItem key={value} value={value}>
											{description}
										</CerebroDropdownMenuItem>
									))}
								</Field>
							</Grid>
							{values.successFeeType == 'Flat' ? (
								<>
									<Grid item xs={4}>
										<CerebroLabel required id="basisPoints">
											Basis Points
										</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											id="basisPoints"
											name="basisPoints"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('basisPoints', errors, touched)}
										/>
										{FormikUtils.IsInvalid('basisPoints', errors, touched) && (
											<CerebroText variant="error">{errors.basisPoints}</CerebroText>
										)}
									</Grid>
									<Grid item xs={3}></Grid>
								</>
							) : (
								<>
									{values.successFeeType == 'Tiered' ? (
										<Grid item xs={7}>
											<CerebroText variant="hint" sx={{ marginTop: 5 }}>
												Add tier details after saving subscription
											</CerebroText>
										</Grid>
									) : (
										<Grid item xs={7}></Grid>
									)}
								</>
							)}

							{values.successFeeType !== 'None' && (
								<>
									<Grid item xs={6}>
										<CerebroLabel id="successFeePaymentSchedule">
											Success Fee Payment Terms
										</CerebroLabel>
										<Field
											id="successFeePaymentSchedule"
											as={CerebroDropdown}
											name="successFeePaymentSchedule"
											disabled={isSubmitting}
										>
											{Object.entries(PaymentScheduleTypeMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>

									<Grid item xs={6}>
										<CerebroLabel id="successFeeCap">Success Fee Cap</CerebroLabel>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="successFeeCap"
											name="successFeeCap"
											disabled={isSubmitting}
										/>
									</Grid>

									<Grid item xs={6}>
										<CerebroLabel id="refinanceBenefitType">Refinance Benefit Type</CerebroLabel>
										<Field
											id="refinanceBenefitType"
											as={CerebroDropdown}
											name="refinanceBenefitType"
											disabled={isSubmitting}
										>
											{Object.entries(RefinanceBenefitTypeMap).map(([value, description]) => (
												<CerebroDropdownMenuItem key={value} value={value}>
													{description}
												</CerebroDropdownMenuItem>
											))}
										</Field>
									</Grid>
									{values.refinanceBenefitType == 'PercentDiscount' && (
										<Grid item xs={6}>
											<CerebroLabel id="refinanceSuccessFeeDiscount">
												Refinance Success Fee Discount
											</CerebroLabel>
											<Field
												formatas="percent"
												as={CerebroMaskedInput}
												id="refinanceSuccessFeeDiscount"
												name="refinanceSuccessFeeDiscount"
												disabled={isSubmitting}
											/>
										</Grid>
									)}
									<Grid item xs={12}>
										<CerebroLabel id="includeSbaExclusion">Include SBA Exclusion?</CerebroLabel>
										<Field
											as={CerebroYesNo}
											id="includeSbaExclusion"
											name="includeSbaExclusion"
											disabled={isSubmitting}
										/>
									</Grid>
								</>
							)}

							<Grid item xs={12}>
								<CerebroLabel htmlFor="isDirectIntroDeal" sx={{ marginTop: 6 }}>
									Is this a direct intro deal?
								</CerebroLabel>
								<Field
									id="isDirectIntroDeal"
									as={CerebroYesNo}
									name="isDirectIntroDeal"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={12}>
								<CerebroLabel id="autoRenew">Auto Renew</CerebroLabel>
								<Field as={CerebroYesNo} id="autoRenew" name="autoRenew" disabled={isSubmitting} />
							</Grid>
							<Grid item xs={12}>
								<CerebroLabel id="hasIncentivePricing">Has Incentive Pricing?</CerebroLabel>
								<Field
									as={CerebroYesNo}
									id="hasIncentivePricing"
									name="hasIncentivePricing"
									disabled={isSubmitting}
								/>
							</Grid>
							{values.hasIncentivePricing && (
								<>
									<Grid item xs={5}>
										<CerebroLabel required id="incentiveSubscriptionFee">
											Incentive Fee
										</CerebroLabel>
										<Field
											formatas="currency"
											as={CerebroMaskedInput}
											maxLength={MaxCurrencyLength}
											id="incentiveSubscriptionFee"
											name="incentiveSubscriptionFee"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('incentiveSubscriptionFee', errors, touched)}
										/>
										{FormikUtils.IsInvalid('incentiveSubscriptionFee', errors, touched) && (
											<CerebroText variant="error">{errors.incentiveSubscriptionFee}</CerebroText>
										)}
									</Grid>
									<Grid item xs={5}>
										<CerebroLabel required id="incentiveExpirationDate">
											Incentive Expiration Date
										</CerebroLabel>
										<Field
											component={CerebroDatePicker}
											id="incentiveExpirationDate"
											name="incentiveExpirationDate"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid('incentiveExpirationDate', errors, touched)}
										/>
										{FormikUtils.IsInvalid('incentiveExpirationDate', errors, touched) && (
											<CerebroText variant="error">{errors.incentiveExpirationDate}</CerebroText>
										)}
									</Grid>
									<Grid item xs={2}></Grid>
								</>
							)}
							<Grid item xs={6}>
								<CerebroLabel required id="excludedLendersCount">
									Excluded Lenders Count
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="excludedLendersCount"
									name="excludedLendersCount"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={6}></Grid>

							<Grid item xs={values.successFeeType != 'None' ? 6 : 11}>
								<CerebroLabel required id="reducedFeeLendersCount">
									Reduced Fee Lenders Count
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="reducedFeeLendersCount"
									name="reducedFeeLendersCount"
									disabled={isSubmitting}
								/>
							</Grid>

							{values.successFeeType != 'None' && (
								<>
									<Grid item xs={6}>
										<CerebroLabel id="reducedFeeLenderDiscountPercent">
											Success Fee Discount for Reduced Fee Lenders
										</CerebroLabel>
										<Field
											as={CerebroMaskedInput}
											formatas="percent"
											id="reducedFeeLenderDiscountPercent"
											name="reducedFeeLenderDiscountPercent"
											disabled={isSubmitting}
											error={FormikUtils.IsInvalid(
												'reducedFeeLenderDiscountPercent',
												errors,
												touched
											)}
										/>
										<CerebroText variant="hint">
											When deal closed with a reduced fee lender this is the percentage discount
											the borrower will pay on success fee.
										</CerebroText>
										{FormikUtils.IsInvalid('reducedFeeLenderDiscountPercent', errors, touched) && (
											<CerebroText variant="error">
												{errors.reducedFeeLenderDiscountPercent}
											</CerebroText>
										)}
									</Grid>
								</>
							)}

							<Grid item xs={6}>
								<CerebroLabel required id="tailPeriodMonths">
									Tail Period (months)
								</CerebroLabel>
								<Field
									as={CerebroMaskedInput}
									id="tailPeriodMonths"
									name="tailPeriodMonths"
									disabled={isSubmitting}
								/>
							</Grid>

							<Grid item xs={6}></Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

var validateForm = (values: CreateCompanySubscriptionFormValues): CreateCompanySubscriptionFormErrors => {
		const errors: CreateCompanySubscriptionFormErrors = {};

		if (!values.term) {
			errors.term = 'Term is required';
		}
		if (!values.guaranteeTerms) {
			errors.guaranteeTerms = 'Guaranteed Term is required';
		}
		if (!values.dealsPerYear) {
			errors.dealsPerYear = 'Deals per year is required';
		}
		if (!values.subscriptionFee) {
			errors.subscriptionFee = 'Subscription fee is required';
		}
		if (!values.additionalDealFee) {
			errors.additionalDealFee = 'Addition deal fee is required';
		}
		if (values.successFeeType == 'Flat' && !values.basisPoints) {
			errors.basisPoints = 'Basis points is required';
		}
		if (values.hasIncentivePricing) {
			if (!values.incentiveSubscriptionFee) {
				errors.incentiveSubscriptionFee = 'Incentive fee is required';
			}
			if (!values.incentiveExpirationDate) {
				errors.incentiveExpirationDate = 'Incentive expiration date is required';
			}
		}
		const dateError = validateDateWithinPlusMinus100Years(
			values.incentiveExpirationDate ?? null,
			'Incentive Expiration Date'
		);

		if (dateError) {
			errors.incentiveExpirationDate = dateError;
		}

		return errors;
	};

type CreateCompanySubscriptionFormErrors = FormikErrors<CreateCompanySubscriptionFormValues>;
type CreateCompanySubscriptionMutationVariables =
	FormikUtils.MutateOnSubmitVariables<CreateCompanySubscriptionFormValues>;
interface CreateCompanySubscriptionFormValues {
	term: string;
	guaranteeTerms: string;
	dealsPerYear: string;
	autoRenew: boolean;
	subscriptionFee: string;
	firstYearSubscriptionFee: string;
	subscriptionFeePaymentSchedule: PaymentScheduleType;
	additionalDealFee: string;
	additionalDealFeePaymentSchedule: PaymentScheduleType;
	billingFrequency: SubscriptionFrequency;
	successFeeType: SuccessFeeType;
	basisPoints: string;
	hasIncentivePricing: boolean;
	incentiveSubscriptionFee: string;
	incentiveExpirationDate: Date | null;
	companyName: string | null;
	city: string;
	state: string;
	address1: string;
	postalCode: string;
	successFeePaymentSchedule: PaymentScheduleType;
	isDirectIntroDeal: boolean;
	successFeeCap: string;
	includeSbaExclusion: boolean;
	refinanceSuccessFeeDiscount: string;
	refinanceBenefitType: RefinanceBenefitType;
	excludedLendersCount: string;
	reducedFeeLenderDiscountPercent: string;
	reducedFeeLendersCount: string;
	tailPeriodMonths: string;
}
